import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, ExternalLink, AlertCircle, X } from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface Citation {
  id: string;
  documentId: string;
  chunkId: string;
  filename: string;
  section?: string;
  sectionType?: string;
  excerpt: string;
  fullContent?: string;
  isImportant?: boolean;
  relevance: number;
}

interface CitationBadgeProps {
  citation: Citation;
  onOpenDocument?: (documentId: string, chunkId?: string) => void;
}

/**
 * CitationBadge - A small badge that shows citation number with hover preview
 */
export const CitationBadge: React.FC<CitationBadgeProps> = ({ citation, onOpenDocument }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const badgeRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = () => {
    if (badgeRef.current) {
      const rect = badgeRef.current.getBoundingClientRect();
      setPreviewPosition({
        x: rect.left,
        y: rect.bottom + 8
      });
    }
    setShowPreview(true);
  };

  const handleMouseLeave = () => {
    setShowPreview(false);
  };

  const handleClick = () => {
    console.log('🔍 [CitationBadge] Clicked!', { 
      documentId: citation.documentId, 
      chunkId: citation.chunkId,
      filename: citation.filename 
    });
    if (onOpenDocument) {
      onOpenDocument(citation.documentId, citation.chunkId);
    }
  };

  return (
    <>
      <span
        ref={badgeRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-medium text-gray-500 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 hover:text-gray-700 transition-colors align-super ml-0.5"
      >
        {citation.id.replace('cite_', '')}
      </span>

      <AnimatePresence>
        {showPreview && (
          <CitationPreviewPortal
            citation={citation}
            position={previewPosition}
            onClose={() => setShowPreview(false)}
            onOpenDocument={onOpenDocument}
          />
        )}
      </AnimatePresence>
    </>
  );
};

/**
 * CitationPreviewPortal - Portal-rendered preview popup
 */
const CitationPreviewPortal: React.FC<{
  citation: Citation;
  position: { x: number; y: number };
  onClose: () => void;
  onOpenDocument?: (documentId: string, chunkId?: string) => void;
}> = ({ citation, position, onClose, onOpenDocument }) => {
  // Adjust position to keep within viewport
  const adjustedX = Math.min(position.x, window.innerWidth - 340);
  const adjustedY = Math.min(position.y, window.innerHeight - 250);

  return (
    <motion.div
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -5 }}
      transition={{ duration: 0.15 }}
      style={{
        position: 'fixed',
        left: adjustedX,
        top: adjustedY,
        zIndex: 9999
      }}
      className="w-80"
      onMouseEnter={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium text-gray-900 truncate block">{citation.filename}</span>
            {citation.section && (
              <span className="text-[10px] text-gray-500 block">{citation.section}</span>
            )}
          </div>
          {citation.isImportant && (
            <span className="px-1.5 py-0.5 text-[9px] font-medium bg-amber-100 text-amber-700 rounded">
              Viktig
            </span>
          )}
        </div>

        {/* Content */}
        <div className="p-3">
          <span className="text-xs text-gray-700 leading-relaxed line-clamp-6 block">
            {citation.fullContent || citation.excerpt}
          </span>
        </div>

        {/* Footer */}
        <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
          <span className="text-[10px] text-gray-400">
            Relevans: {Math.round(citation.relevance * 100)}%
          </span>
          {onOpenDocument && (
            <button
              onClick={() => onOpenDocument(citation.documentId, citation.chunkId)}
              className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 font-medium"
            >
              Åpne dokument
              <ExternalLink className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/**
 * CitationList - List of all citations at the bottom of AI response
 */
interface CitationListProps {
  citations: Citation[];
  onOpenDocument?: (documentId: string, chunkId?: string) => void;
}

export const CitationList: React.FC<CitationListProps> = ({ citations, onOpenDocument }) => {
  if (!citations || citations.length === 0) return null;

  return (
    <div className="mt-4 pt-3 border-t border-gray-100">
      <span className="text-xs font-medium text-gray-500 mb-2 block">Referanser</span>
      <div className="space-y-1.5">
        {citations.map((citation) => (
          <div
            key={citation.id}
            onClick={() => onOpenDocument?.(citation.documentId, citation.chunkId)}
            className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors group"
          >
            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center text-[10px] font-medium text-gray-500 bg-gray-100 rounded">
              {citation.id.replace('cite_', '')}
            </span>
            <div className="flex-1 min-w-0">
              <span className="text-xs font-medium text-gray-900 truncate block group-hover:text-gray-700">
                {citation.filename}
                {citation.section && <span className="text-gray-400 ml-1">· {citation.section}</span>}
              </span>
              <span className="text-[11px] text-gray-500 line-clamp-1 block">{citation.excerpt}</span>
            </div>
            {citation.isImportant && (
              <AlertCircle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * AIResponseWithCitations - Renders AI response with inline citation badges
 */
interface AIResponseWithCitationsProps {
  response: string;
  citations: Citation[];
  onOpenDocument?: (documentId: string, chunkId?: string) => void;
}

export const AIResponseWithCitations: React.FC<AIResponseWithCitationsProps> = ({
  response,
  citations,
  onOpenDocument
}) => {
  // Parse response and replace [cite_X] with CitationBadge components
  const parsedContent = React.useMemo(() => {
    if (!citations || citations.length === 0) {
      return <p className="whitespace-pre-wrap">{response}</p>;
    }

    const citationMap = new Map(citations.map(c => [c.id, c]));
    const parts: React.ReactNode[] = [];
    
    // Match [cite_1], [cite_2], etc.
    const regex = /\[cite_(\d+)\]/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(response)) !== null) {
      // Add text before citation
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>
            {response.slice(lastIndex, match.index)}
          </span>
        );
      }

      // Add citation badge
      const citationId = `cite_${match[1]}`;
      const citation = citationMap.get(citationId);
      
      if (citation) {
        parts.push(
          <CitationBadge
            key={`cite-${match.index}`}
            citation={citation}
            onOpenDocument={onOpenDocument}
          />
        );
      } else {
        // Citation not found, show as text
        parts.push(
          <span key={`unknown-${match.index}`} className="text-gray-400">
            {match[0]}
          </span>
        );
      }

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < response.length) {
      parts.push(
        <span key={`text-end`}>
          {response.slice(lastIndex)}
        </span>
      );
    }

    return <div className="whitespace-pre-wrap">{parts}</div>;
  }, [response, citations, onOpenDocument]);

  return (
    <div>
      {parsedContent}
      <CitationList citations={citations} onOpenDocument={onOpenDocument} />
    </div>
  );
};

/**
 * DocumentPreviewModal - Full document preview with highlighted section
 */
interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  chunkId?: string;
  highlightText?: string;
}

export const DocumentPreviewModal: React.FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  documentId,
  chunkId,
  highlightText
}) => {
  const [loading, setLoading] = useState(true);
  const [document, setDocument] = useState<any>(null);
  const [chunk, setChunk] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen && documentId) {
      fetchDocumentAndChunk();
    }
  }, [isOpen, documentId, chunkId]);

  const fetchDocumentAndChunk = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch document info
      const docRes = await axios.get(`${API}/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${auth.getToken()}` }
      });
      setDocument(docRes.data);

      // Fetch chunk if specified
      if (chunkId) {
        const chunkRes = await axios.get(`${API}/documents/${documentId}/chunk/${chunkId}`, {
          headers: { Authorization: `Bearer ${auth.getToken()}` }
        });
        setChunk(chunkRes.data);
      }
    } catch (err) {
      setError('Kunne ikke laste dokument');
      console.error('Error loading document:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-gray-400" />
            <div>
              <h2 className="font-semibold text-gray-900">{document?.filename || 'Dokument'}</h2>
              {chunk?.section_number && (
                <p className="text-sm text-gray-500">{chunk.section_number} {chunk.title}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="text-center text-red-500 py-8">{error}</div>
          ) : chunk ? (
            <div>
              {/* Highlighted section */}
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg mb-4">
                <p className="text-xs font-medium text-amber-700 mb-2">
                  {chunk.section_number || 'Referert seksjon'}
                  {chunk.is_important && ' · Viktig klausul'}
                </p>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{chunk.content}</p>
              </div>

              {/* Keywords */}
              {chunk.keywords && chunk.keywords.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {chunk.keywords.map((kw: string, i: number) => (
                    <span key={i} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                      {kw}
                    </span>
                  ))}
                </div>
              )}

              {/* References */}
              {chunk.references && chunk.references.length > 0 && (
                <div className="text-xs text-gray-500">
                  <span className="font-medium">Referanser: </span>
                  {chunk.references.join(', ')}
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Ingen innhold å vise</p>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            Lukk
          </button>
          {document && (
            <a
              href={`${API}/documents/${documentId}/download`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg inline-flex items-center gap-2"
            >
              Last ned
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AIResponseWithCitations;
